import { IonInfiniteScroll } from '@ionic/react'
import { motion } from 'framer-motion'
import { useMemo } from 'react'

import { useGetUnitTriggerHistoryByTrigger } from '@apps-common/apis/portal/trigger'
import { InfiniteLoader } from '@apps-common/components/common/InfiniteLoader'
import { PullRefresh } from '@apps-common/components/common/PullRefresh'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { IUnitTriggerQueueBasicModel } from '@apps-common/types/portal/unit-trigger'

import { useMyMonitoringGetUnitTriggerHistoryByTrigger } from '@apps-common/apis/my-monitoring/triggers'
import { getControlType } from '@apps-common/apps/portal/pages/units/pages/manage/components/controls/control-type'
import { CONTROL_TYPE_ACCESS_CONTROL, CONTROL_TYPE_CUSTOM } from '@apps-common/config/controls'
import { useControlTheme } from '@apps-common/hooks/useControlTheme'
import { ENV_APP_PROJECT } from '@apps-common/lib/environment'
import { HistorySkeletonCard } from './HistorySkeletonCard'
import { HistoryDate } from './components/HistoryDate'
import { HistoryResultMessageDisplay } from './components/HistoryResultMessageDisplay'
import { HistoryState } from './components/HistoryState'
import { HistoryStatus } from './components/HistoryStatus'

const TAKE = 15
const project = ENV_APP_PROJECT
interface IProps {
  triggerId: number
  unitId?: string
}

export const TriggerHistoryView: React.FC<IProps> = ({ triggerId, unitId }) => {
  const { resolve } = useControlTheme()

  const useTriggerHistoryHook = useMemo(() => {
    if (project === 'my-monitoring') {
      return useMyMonitoringGetUnitTriggerHistoryByTrigger
    }

    return useGetUnitTriggerHistoryByTrigger
  }, [])

  const {
    isLoading: isLoadingById,
    isFetching: isFetchingById,
    isRefetching: isRefetchingById,
    isError: isErrorById,
    data: dataById,
    refetch: refetchById,
    fetchNextPage: fetchNextPageById,
    hasNextPage: hasNextPageById
  } = useTriggerHistoryHook({ triggerId, take: TAKE, enabled: unitId === undefined })

  const data = useMemo(
    () =>
      dataById?.pages.reduce((acc: IUnitTriggerQueueBasicModel[], curr: IUnitTriggerQueueBasicModel[]) => [...acc, ...(curr || [])], []),
    [unitId, dataById]
  )

  const loading = isLoadingById || isErrorById

  return (
    <>
      <PullRefresh loading={isRefetchingById} callback={refetchById} />
      <div className='flex flex-col gap-[10px]'>
        {(loading || !data) && Array.from(Array(TAKE).keys()).map((i) => <HistorySkeletonCard key={i.toString()} />)}
        {!loading &&
          data &&
          data.map((d, i) => {
            // check if custom control
            // set to requires control state to avoid
            // neutral colors for state displaying
            const requiresControlState = d.controlTypeId === CONTROL_TYPE_CUSTOM ? true : d.requiresControlState
            const typeId = d.controlTypeId === CONTROL_TYPE_CUSTOM ? CONTROL_TYPE_ACCESS_CONTROL : d.controlTypeId
            const controlType = getControlType(typeId, requiresControlState)
            const colors = resolve(controlType, d.desiredState.toLowerCase())

            return (
              <motion.div
                key={`control-history-card-${d.id}-${i}`}
                {...motionFadeIn(0.3)}
                className='relative overflow-hidden w-full bg-surface text-text border border-border rounded-[24px] pl-[15px] py-[10px] flex items-end justify-between'
              >
                <div className='flex flex-col gap-[10px] items-start justify-between'>
                  <HistoryDate date={d.dateAdded} />
                  <div className='text-[0.85em] flex items-center justify-start gap-[8px]'>
                    <HistoryStatus result={d.result} msg={d.msg} />
                  </div>
                </div>
                <HistoryState colors={colors} state={d.desiredState} />
              </motion.div>
            )
          })}
        <HistoryResultMessageDisplay
          hasNoResults={!loading && !isFetchingById && !hasNextPageById && data && data.length === 0}
          isEndOfResults={!loading && !isFetchingById && !hasNextPageById && data && data.length > 0}
        />
      </div>
      <IonInfiniteScroll
        onIonInfinite={async (ev) => {
          if (!hasNextPageById) {
            ev.target.complete()

            return
          }

          await fetchNextPageById()

          ev.target.complete()
        }}
      >
        <InfiniteLoader key='trigger-history-infinite-loader' loading={!loading && isFetchingById} />
      </IonInfiniteScroll>
    </>
  )
}
